// src/pages/analytics/TaxAnalytics.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AccountBalance as TaxIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as ChartTooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

// Services
import taxConfigService from '../../services/taxConfigService';
import { api } from '../../services/api';

// Components
import PageHeader from '../../components/ui/PageHeader';
import MagicLoading from '../../components/ui/MagicLoading';
import MetricCard from '../../components/ui/MetricCard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const TaxAnalytics = () => {
  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(subMonths(new Date(), 11)),
    endDate: endOfMonth(new Date())
  });
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [taxConfigs, setTaxConfigs] = useState([]);
  
  // Analytics data
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalTaxCollected: 0,
      totalTaxableAmount: 0,
      averageTaxRate: 0,
      transactionCount: 0
    },
    trends: [],
    breakdown: {
      byTaxType: [],
      byRegion: [],
      byMonth: []
    },
    topTaxPayers: [],
    compliance: {
      payrollTaxes: 0,
      invoiceTaxes: 0,
      withholdingTaxes: 0,
      vatCollected: 0
    }
  });

  // Load data on component mount
  useEffect(() => {
    loadTaxConfigurations();
    loadAnalyticsData();
  }, [dateRange, selectedRegion]);

  // Load tax configurations
  const loadTaxConfigurations = async () => {
    try {
      const response = await taxConfigService.getAllTaxConfigs();
      setTaxConfigs(response?.data || []);
    } catch (error) {
      console.error('Error loading tax configurations:', error);
    }
  };

  // Load analytics data
  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch tax analytics from API
      const response = await api.get('/analytics/tax', {
        params: {
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
          region: selectedRegion !== 'all' ? selectedRegion : undefined
        }
      });
      
      setAnalyticsData(response.data?.data || {
        overview: {
          totalTaxCollected: 0,
          totalTaxableAmount: 0,
          averageTaxRate: 0,
          transactionCount: 0
        },
        trends: [],
        breakdown: {
          byTaxType: [],
          byRegion: [],
          byMonth: []
        },
        topTaxPayers: [],
        compliance: {
          payrollTaxes: 0,
          invoiceTaxes: 0,
          withholdingTaxes: 0,
          vatCollected: 0
        }
      });
      
    } catch (error) {
      console.error('Error loading tax analytics:', error);
      // Set default empty data on error
      setAnalyticsData({
        overview: {
          totalTaxCollected: 0,
          totalTaxableAmount: 0,
          averageTaxRate: 0,
          transactionCount: 0
        },
        trends: [],
        breakdown: {
          byTaxType: [],
          byRegion: [],
          byMonth: []
        },
        topTaxPayers: [],
        compliance: {
          payrollTaxes: 0,
          invoiceTaxes: 0,
          withholdingTaxes: 0,
          vatCollected: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Export analytics data
  const exportAnalytics = async () => {
    try {
      const response = await api.get('/analytics/tax/export', {
        params: {
          startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
          endDate: format(dateRange.endDate, 'yyyy-MM-dd'),
          region: selectedRegion !== 'all' ? selectedRegion : undefined,
          format: 'xlsx'
        },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax-analytics-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting analytics:', error);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  if (loading) {
    return <MagicLoading />;
  }

  return (
    <Box>
      <PageHeader
        title="Tax Analytics"
        subtitle="Comprehensive tax reporting and analysis across all modules"
        actions={[
          {
            label: 'Export',
            icon: <DownloadIcon />,
            onClick: exportAnalytics
          },
          {
            label: 'Refresh',
            icon: <RefreshIcon />,
            onClick: loadAnalyticsData
          }
        ]}
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
                  renderInput={(props) => <TextField {...props} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
                  renderInput={(props) => <TextField {...props} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Tax Region</InputLabel>
                <Select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  label="Tax Region"
                >
                  <MenuItem value="all">All Regions</MenuItem>
                  {taxConfigs.map((config) => (
                    <MenuItem key={config._id} value={config.regionCode}>
                      {config.regionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Tax Collected"
            value={formatCurrency(analyticsData.overview.totalTaxCollected)}
            icon={<TaxIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Taxable Amount"
            value={formatCurrency(analyticsData.overview.totalTaxableAmount)}
            icon={<ReceiptIcon />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average Tax Rate"
            value={formatPercentage(analyticsData.overview.averageTaxRate)}
            icon={<TrendingUpIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Tax Transactions"
            value={analyticsData.overview.transactionCount?.toLocaleString() || '0'}
            icon={<AssessmentIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Card>
        <CardContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Trends" />
            <Tab label="Breakdown" />
            <Tab label="Compliance" />
            <Tab label="Details" />
          </Tabs>

          {/* Trends Tab */}
          {activeTab === 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tax Collection Trends
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={analyticsData.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <ChartTooltip formatter={(value) => formatCurrency(value)} />
                  <Area 
                    type="monotone" 
                    dataKey="totalTax" 
                    stroke="#8884d8" 
                    fill="#8884d8" 
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Breakdown Tab */}
          {activeTab === 1 && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={3}>
                {/* Tax Type Breakdown */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Tax by Type
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analyticsData.breakdown.byTaxType}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {analyticsData.breakdown.byTaxType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <ChartTooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Grid>

                {/* Regional Breakdown */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Tax by Region
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analyticsData.breakdown.byRegion}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="region" />
                      <YAxis />
                      <ChartTooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="amount" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Compliance Tab */}
          {activeTab === 2 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tax Compliance Overview
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Payroll Taxes
                      </Typography>
                      <Typography variant="h4">
                        {formatCurrency(analyticsData.compliance.payrollTaxes)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Invoice Taxes
                      </Typography>
                      <Typography variant="h4">
                        {formatCurrency(analyticsData.compliance.invoiceTaxes)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Withholding Taxes
                      </Typography>
                      <Typography variant="h4">
                        {formatCurrency(analyticsData.compliance.withholdingTaxes)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        VAT Collected
                      </Typography>
                      <Typography variant="h4">
                        {formatCurrency(analyticsData.compliance.vatCollected)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Details Tab */}
          {activeTab === 3 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Top Tax Contributors
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Tax Amount</TableCell>
                      <TableCell align="right">Tax Rate</TableCell>
                      <TableCell>Region</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.topTaxPayers.map((payer, index) => (
                      <TableRow key={index}>
                        <TableCell>{payer.entityName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={payer.type} 
                            size="small"
                            color={payer.type === 'Payroll' ? 'primary' : 
                                   payer.type === 'Invoice' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(payer.taxAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatPercentage(payer.taxRate)}
                        </TableCell>
                        <TableCell>{payer.region}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaxAnalytics;